import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPreferences,
  setPreferences,
  type UserPreferences,
} from '@/services/api/endpoints';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';

/** Ce que vaut une préférence tant qu'on ne l'a pas lue. Cf. `usePreference`. */
export const PREFERENCE_DEFAULTS: UserPreferences = {
  deleteAfterFiling: false,
  autoCategorize: false,
  categorizeProjectNumber: false,
  sendAndFile: false,
};

export const PREFERENCES_KEY = ['preferences'] as const;

export function usePreferences() {
  return useQuery<UserPreferences>({
    queryKey: PREFERENCES_KEY,
    queryFn: getPreferences,
    // `staleTime: 0` À DESSEIN, contrairement aux 5 min de `whoami`. Le cache
    // TanStack est PERSISTÉ sur le disque (prism-qc-v1, 7 jours) : une valeur
    // rejouée depuis le disque pourrait réactiver « supprimer après rangement »
    // alors que l'utilisateur l'a désactivé sur un autre poste. On revalide
    // donc à chaque montage ; la valeur en cache sert seulement à éviter un
    // écran vide pendant la requête.
    staleTime: 0,
  });
}

/**
 * La valeur d'UNE préférence, utilisable dans un chemin d'action.
 *
 * Renvoie le défaut (faux) tant que la requête n'a pas abouti — et aussi quand
 * elle échoue. C'est le comportement voulu : ces préférences déclenchent des
 * effets sur la boîte mail, donc « je ne sais pas encore » doit se comporter
 * comme « désactivé », jamais l'inverse. Une panne du back-end ne doit pas
 * pouvoir faire supprimer des mails.
 */
export function usePreference<K extends keyof UserPreferences>(key: K): boolean {
  const { data } = usePreferences();
  return data?.[key] ?? PREFERENCE_DEFAULTS[key];
}

export function useSetPreference() {
  const qc = useQueryClient();
  return useMutation<UserPreferences, Error, Partial<UserPreferences>>({
    mutationFn: (patch) => {
      // Les clés seules, pas les valeurs : une préférence n'est pas une donnée
      // personnelle, mais on garde la trace du réglage MODIFIÉ, pas de l'état.
      addBreadcrumb({
        category: 'mutation',
        message: 'set-preference',
        data: { keys: Object.keys(patch).join(',') },
      });
      return setPreferences(patch);
    },
    // La réponse EST l'état complet après écriture : on l'installe directement
    // plutôt que d'invalider, sinon l'interrupteur reviendrait visuellement en
    // arrière le temps d'un aller-retour.
    onSuccess: (fresh) => qc.setQueryData(PREFERENCES_KEY, fresh),
  });
}
